/**
 * CRMPage.js  –  Guest CRM / History / Loyalty / Complaints / NPS
 * UI v3 — Redesigned with richer cards, visual hierarchy, NPS gauge, tier badges
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';

const gold   = '#C9A84C';
const green  = '#2ecc71';
const red    = '#e74c3c';
const blue   = '#3498db';
const orange = '#f39c12';
const purple = '#9b59b6';
const silver = '#95a5a6';

const TIER_COLOR = { standard: silver, silver, gold, platinum: '#e8e0d5' };
const TIER_BG    = { standard: '#1a1a1a', silver: '#1a1e22', gold: '#1e1a0e', platinum: '#1a1c20' };
const TIER_EMOJI = { standard: '⬜', silver: '🔘', gold: '🥇', platinum: '💎' };
const TIER_LABEL = { standard: 'Standard', silver: 'Silver', gold: 'Gold', platinum: 'Platinum' };

const COMPLAINT_CATS = ['room_quality','food','service','noise','cleanliness','billing','amenities','other'];
const STATUS_COLOR = { open: red, in_progress: orange, resolved: green, escalated: purple };
const STATUS_ICON  = { open: '🔴', in_progress: '🟡', resolved: '✅', escalated: '🟣' };

const blankProfile  = { name:'', id_number:'', phone:'', email:'', nationality:'', preferences:'', notes:'' };
const blankComplaint= { profile:'', category:'service', description:'', date_reported: new Date().toISOString().split('T')[0] };
const blankNPS      = { profile:'', score:'', comment:'' };

function Modal({ onClose, children, width = 560 }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.82)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',overflow:'auto',padding:20 }}>
      <div style={{ background:'var(--surface)',borderRadius:16,width,maxWidth:'95vw',boxShadow:'0 32px 100px rgba(0,0,0,0.7)',border:'1px solid rgba(201,168,76,0.12)',animation:'crmSlide 0.18s ease' }}>
        {children}
      </div>
      <style>{`@keyframes crmSlide{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

function NPSGauge({ score }) {
  if (score == null) return <div style={{ fontSize:32, fontWeight:800, color:'var(--textMuted)' }}>—</div>;
  const clamped = Math.max(-100, Math.min(100, score));
  const pct = ((clamped + 100) / 200) * 100;
  const col = score >= 50 ? green : score >= 0 ? gold : red;
  return (
    <div style={{ textAlign:'center' }}>
      <div style={{ fontSize:40, fontWeight:900, color:col, lineHeight:1 }}>{score}</div>
      <div style={{ margin:'8px 0 4px', height:8, background:'rgba(255,255,255,0.08)', borderRadius:8, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${pct}%`, background:col, borderRadius:8, transition:'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize:11, color:col, fontWeight:600 }}>
        {score >= 50 ? 'Excellent 🌟' : score >= 20 ? 'Good 👍' : score >= 0 ? 'Needs Work ⚠️' : 'Critical 🚨'}
      </div>
    </div>
  );
}

function TierBadge({ tier, size = 'md' }) {
  const fs = size === 'sm' ? 10 : 12;
  const px = size === 'sm' ? '3px 9px' : '4px 12px';
  return (
    <span style={{ fontSize:fs, fontWeight:700, padding:px, borderRadius:20,
      background:TIER_BG[tier], color:TIER_COLOR[tier], border:`1px solid ${TIER_COLOR[tier]}40`, letterSpacing:'0.04em' }}>
      {TIER_EMOJI[tier]} {TIER_LABEL[tier]}
    </span>
  );
}

export default function CRMPage({ notify, userRole }) {
  const [tab, setTab]               = useState('profiles');
  const [profiles, setProfiles]     = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [summary, setSummary]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [profileForm, setProfileForm] = useState(blankProfile);
  const [editProfile, setEditProfile] = useState(null);
  const [showCmpForm, setShowCmpForm] = useState(false);
  const [complaintForm, setComplaintForm] = useState(blankComplaint);
  const [showNPSForm, setShowNPSForm] = useState(false);
  const [npsForm, setNpsForm]       = useState(blankNPS);
  const [search, setSearch]         = useState('');
  const [tierFilter, setTierFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, sRes] = await Promise.all([api.getCRMProfiles(), api.getComplaints(), api.getCRMSummary()]);
      setProfiles(pRes.data.results || pRes.data || []);
      setComplaints(cRes.data.results || cRes.data || []);
      setSummary(sRes.data);
    } catch { notify?.('Failed to load CRM data', 'error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    if (!profileForm.name || !profileForm.id_number) { notify?.('Name and ID required', 'error'); return; }
    try {
      if (editProfile) { await api.updateCRMProfile(editProfile.id, profileForm); notify?.('Profile updated'); }
      else             { await api.createCRMProfile(profileForm); notify?.('Guest profile created'); }
      setShowForm(false); setEditProfile(null); setProfileForm(blankProfile); load();
    } catch (e) { notify?.(e?.response?.data?.id_number?.[0] || 'Failed to save profile', 'error'); }
  };

  const saveComplaint = async () => {
    if (!complaintForm.profile || !complaintForm.description) { notify?.('Profile and description required', 'error'); return; }
    try { await api.createComplaint(complaintForm); notify?.('Complaint recorded'); setShowCmpForm(false); setComplaintForm(blankComplaint); load(); }
    catch { notify?.('Failed to save complaint', 'error'); }
  };

  const submitNPS = async () => {
    if (!npsForm.profile || npsForm.score === '') { notify?.('Profile and score required', 'error'); return; }
    const score = parseInt(npsForm.score);
    if (score < 0 || score > 10) { notify?.('Score must be 0–10', 'error'); return; }
    try { await api.createNPS({ ...npsForm, score }); notify?.('NPS score submitted'); setShowNPSForm(false); setNpsForm(blankNPS); load(); }
    catch { notify?.('Failed to submit NPS', 'error'); }
  };

  const resolveComplaint = async (id) => {
    const resolution = window.prompt('Enter resolution notes:');
    if (resolution === null) return;
    try { await api.resolveComplaint(id, { resolution }); notify?.('Complaint resolved'); load(); }
    catch { notify?.('Failed to resolve', 'error'); }
  };

  const openEdit = (p, e) => {
    e.stopPropagation();
    setProfileForm({ name:p.name, id_number:p.id_number, phone:p.phone||'', email:p.email||'', nationality:p.nationality||'', preferences:p.preferences||'', notes:p.notes||'' });
    setEditProfile(p); setShowForm(true);
  };

  const filteredProfiles = profiles.filter(p => {
    const ms = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.id_number.includes(search) || (p.email||'').includes(search) || (p.phone||'').includes(search);
    return ms && (!tierFilter || p.loyalty_tier === tierFilter);
  });

  const tabs = [
    { id:'profiles',   label:'👤 Profiles' },
    { id:'complaints', label:'⚠️ Complaints' },
    { id:'nps',        label:'⭐ NPS' },
    { id:'summary',    label:'📊 Summary' },
  ];

  return (
    <div style={{ padding:'0 0 40px' }}>
      <PageHeader icon="👤" title="Guest CRM"
        sub="Profiles · Loyalty · Complaints · NPS Tracking"
        action={
          <div style={{ display:'flex', gap:8 }}>
            {tab==='profiles'   && <button onClick={()=>{setEditProfile(null);setProfileForm(blankProfile);setShowForm(true);}} style={btnPrimary}>+ New Profile</button>}
            {tab==='complaints' && <button onClick={()=>setShowCmpForm(true)} style={btnPrimary}>+ Log Complaint</button>}
            {tab==='nps'        && <button onClick={()=>setShowNPSForm(true)} style={btnPrimary}>+ Add NPS Score</button>}
          </div>
        }
      />
      <div style={{ padding:'0 20px 14px' }}><TabBar tabs={tabs} active={tab} onChange={setTab} /></div>

      {/* Profile Form Modal */}
      {showForm && (
        <Modal onClose={()=>setShowForm(false)}>
          <div style={{ padding:'24px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <h3 style={{ margin:0, color:gold, fontSize:17, fontWeight:800 }}>{editProfile?'Edit Guest Profile':'New Guest Profile'}</h3>
              <p style={{ margin:'3px 0 0', fontSize:12, color:'var(--textMuted)' }}>Fields marked * are required</p>
            </div>
            <button onClick={()=>setShowForm(false)} style={{ background:'none',border:'none',color:'var(--textMuted)',fontSize:22,cursor:'pointer' }}>✕</button>
          </div>
          <div style={{ padding:'20px 24px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[['name','Full Name',true],['id_number','ID / Passport No.',true],['phone','Phone'],['email','Email'],['nationality','Nationality']].map(([k,l,req])=>(
                <Field key={k} label={l} required={req}>
                  <input value={profileForm[k]||''} onChange={e=>setProfileForm(p=>({...p,[k]:e.target.value}))} style={{...inputStyle,borderRadius:8}} />
                </Field>
              ))}
              <Field label="Preferences / Special Requests" style={{ gridColumn:'1/-1' }}>
                <textarea rows={3} value={profileForm.preferences||''} onChange={e=>setProfileForm(p=>({...p,preferences:e.target.value}))} style={{...inputStyle,minHeight:60,borderRadius:8}} placeholder="Room floor preference, dietary needs, special occasions…" />
              </Field>
              <Field label="Notes" style={{ gridColumn:'1/-1' }}>
                <textarea rows={2} value={profileForm.notes||''} onChange={e=>setProfileForm(p=>({...p,notes:e.target.value}))} style={{...inputStyle,minHeight:50,borderRadius:8}} />
              </Field>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={saveProfile} style={{...btnPrimary,flex:1}}>💾 Save Profile</button>
              <button onClick={()=>setShowForm(false)} style={{...btnSm,flex:1}}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Complaint Form Modal */}
      {showCmpForm && (
        <Modal onClose={()=>setShowCmpForm(false)} width={480}>
          <div style={{ padding:'24px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, color:red, fontSize:17, fontWeight:800 }}>⚠️ Log Guest Complaint</h3>
            <button onClick={()=>setShowCmpForm(false)} style={{ background:'none',border:'none',color:'var(--textMuted)',fontSize:22,cursor:'pointer' }}>✕</button>
          </div>
          <div style={{ padding:'20px 24px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <Field label="Guest Profile" required style={{ gridColumn:'1/-1' }}>
                <select value={complaintForm.profile} onChange={e=>setComplaintForm(p=>({...p,profile:e.target.value}))} style={{...inputStyle,borderRadius:8}}>
                  <option value="">— Select Guest —</option>
                  {profiles.map(p=><option key={p.id} value={p.id}>{p.name} ({p.id_number})</option>)}
                </select>
              </Field>
              <Field label="Category">
                <select value={complaintForm.category} onChange={e=>setComplaintForm(p=>({...p,category:e.target.value}))} style={{...inputStyle,borderRadius:8}}>
                  {COMPLAINT_CATS.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
                </select>
              </Field>
              <Field label="Date Reported">
                <input type="date" value={complaintForm.date_reported} onChange={e=>setComplaintForm(p=>({...p,date_reported:e.target.value}))} style={{...inputStyle,borderRadius:8}} />
              </Field>
              <Field label="Description" required style={{ gridColumn:'1/-1' }}>
                <textarea rows={4} value={complaintForm.description} onChange={e=>setComplaintForm(p=>({...p,description:e.target.value}))} style={{...inputStyle,minHeight:80,borderRadius:8}} placeholder="Describe the issue in detail…" />
              </Field>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={saveComplaint} style={{...btnPrimary,flex:1,background:red}}>Submit Complaint</button>
              <button onClick={()=>setShowCmpForm(false)} style={{...btnSm,flex:1}}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* NPS Form Modal */}
      {showNPSForm && (
        <Modal onClose={()=>setShowNPSForm(false)} width={420}>
          <div style={{ padding:'24px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <h3 style={{ margin:0, color:gold, fontSize:17, fontWeight:800 }}>⭐ Submit NPS Score</h3>
            <button onClick={()=>setShowNPSForm(false)} style={{ background:'none',border:'none',color:'var(--textMuted)',fontSize:22,cursor:'pointer' }}>✕</button>
          </div>
          <div style={{ padding:'20px 24px' }}>
            <Field label="Guest Profile" required>
              <select value={npsForm.profile} onChange={e=>setNpsForm(p=>({...p,profile:e.target.value}))} style={{...inputStyle,borderRadius:8}}>
                <option value="">— Select Guest —</option>
                {profiles.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Score (0–10)" required>
              <div style={{ display:'flex', gap:4, flexWrap:'wrap', marginBottom:8 }}>
                {[0,1,2,3,4,5,6,7,8,9,10].map(n=>{
                  const col = n>=9?green:n>=7?gold:red;
                  const isSel = parseInt(npsForm.score)===n;
                  return (
                    <button key={n} onClick={()=>setNpsForm(p=>({...p,score:String(n)}))}
                      style={{ width:36,height:36,borderRadius:8,border:`2px solid ${isSel?col:'rgba(255,255,255,0.12)'}`,
                        background:isSel?col+'22':'transparent',color:isSel?col:'var(--textMuted)',
                        fontWeight:700,fontSize:13,cursor:'pointer',transition:'all 0.12s' }}>
                      {n}
                    </button>
                  );
                })}
              </div>
              {npsForm.score!=='' && (
                <div style={{ fontSize:12,fontWeight:700,color:parseInt(npsForm.score)>=9?green:parseInt(npsForm.score)>=7?gold:red }}>
                  {parseInt(npsForm.score)>=9?'😊 Promoter':parseInt(npsForm.score)>=7?'😐 Passive':'😞 Detractor'}
                </div>
              )}
            </Field>
            <Field label="Comment">
              <textarea rows={3} value={npsForm.comment} onChange={e=>setNpsForm(p=>({...p,comment:e.target.value}))} style={{...inputStyle,minHeight:60,borderRadius:8}} placeholder="Guest feedback…" />
            </Field>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={submitNPS} style={{...btnPrimary,flex:1}}>Submit Score</button>
              <button onClick={()=>setShowNPSForm(false)} style={{...btnSm,flex:1}}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Profile Detail Modal */}
      {selected && (
        <Modal onClose={()=>setSelected(null)} width={640}>
          <div style={{ padding:'24px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:50,height:50,borderRadius:14,background:`${TIER_COLOR[selected.loyalty_tier]}22`,border:`2px solid ${TIER_COLOR[selected.loyalty_tier]}44`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22 }}>
                {TIER_EMOJI[selected.loyalty_tier]}
              </div>
              <div>
                <h2 style={{ margin:0, color:'var(--text)', fontSize:20, fontWeight:800 }}>{selected.name}</h2>
                <div style={{ marginTop:4, display:'flex', gap:8, alignItems:'center' }}>
                  <TierBadge tier={selected.loyalty_tier} />
                  <span style={{ fontSize:12, color:'var(--textMuted)' }}>{selected.loyalty_points} pts</span>
                </div>
              </div>
            </div>
            <button onClick={()=>setSelected(null)} style={{ background:'none',border:'none',color:'var(--textMuted)',fontSize:22,cursor:'pointer' }}>✕</button>
          </div>
          <div style={{ padding:'20px 24px', maxHeight:'70vh', overflowY:'auto' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
              {[['🏨 Stays',selected.total_stays,gold],['💰 Spend',fmtKES(selected.total_spend),green],['⭐ Avg NPS',selected.avg_nps??'—',blue]].map(([l,v,c])=>(
                <div key={l} style={{ background:'rgba(255,255,255,0.04)',borderRadius:10,padding:'12px 14px',textAlign:'center',border:'1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ color:'var(--textMuted)',fontSize:11,marginBottom:4 }}>{l}</div>
                  <div style={{ fontWeight:800,fontSize:20,color:c }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom:16,background:'rgba(255,255,255,0.03)',borderRadius:10,overflow:'hidden',border:'1px solid rgba(255,255,255,0.07)' }}>
              {[['ID',selected.id_number],['Phone',selected.phone||'—'],['Email',selected.email||'—'],['Nationality',selected.nationality||'—']].map(([l,v],i,arr)=>(
                <div key={l} style={{ display:'flex',justifyContent:'space-between',padding:'10px 14px',borderBottom:i<arr.length-1?'1px solid rgba(255,255,255,0.06)':'none' }}>
                  <span style={{ color:'var(--textMuted)',fontSize:13 }}>{l}</span>
                  <span style={{ color:'var(--text)',fontSize:13,fontWeight:500 }}>{v}</span>
                </div>
              ))}
            </div>
            {selected.preferences && (
              <div style={{ marginBottom:16,padding:'12px 14px',background:`${gold}0d`,border:`1px solid ${gold}30`,borderRadius:10,fontSize:13 }}>
                <div style={{ color:gold,fontWeight:700,marginBottom:6,fontSize:11,textTransform:'uppercase',letterSpacing:'0.08em' }}>Preferences</div>
                <div style={{ color:'var(--textMuted)' }}>{selected.preferences}</div>
              </div>
            )}
            {selected.stay_history?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontWeight:700,color:gold,fontSize:12,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:10 }}>Stay History ({selected.stay_history.length})</div>
                {selected.stay_history.map(s=>(
                  <div key={s.id} style={{ display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,marginBottom:6 }}>
                    <div>
                      <div style={{ color:'var(--text)',fontWeight:600,fontSize:13 }}>{s.check_in} → {s.check_out||'Ongoing'}</div>
                      <div style={{ color:'var(--textMuted)',fontSize:11,marginTop:2 }}>Room {s.room_number||'—'} · {s.purpose} · {s.source?.replace('_',' ')}</div>
                    </div>
                    <div style={{ fontWeight:700,color:green,fontSize:13 }}>{fmtKES(s.total_spend)}</div>
                  </div>
                ))}
              </div>
            )}
            {selected.complaints?.length > 0 && (
              <div>
                <div style={{ fontWeight:700,color:red,fontSize:12,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:10 }}>Complaints ({selected.complaints.length})</div>
                {selected.complaints.map(c=>(
                  <div key={c.id} style={{ padding:'10px 14px',background:`${STATUS_COLOR[c.status]||red}0d`,borderLeft:`3px solid ${STATUS_COLOR[c.status]||red}`,borderRadius:8,marginBottom:6 }}>
                    <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4 }}>
                      <span style={{ fontWeight:700,color:STATUS_COLOR[c.status]||red,fontSize:12 }}>{c.category.replace('_',' ').toUpperCase()}</span>
                      <span style={{ color:'var(--textMuted)',fontSize:11 }}>{c.date_reported}</span>
                    </div>
                    <div style={{ color:'var(--textMuted)',fontSize:12 }}>{c.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Profiles Tab */}
      {tab==='profiles' && (
        <div style={{ padding:'0 20px' }}>
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
            <div style={{ flex:1, minWidth:200, position:'relative' }}>
              <span style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--textMuted)',fontSize:14 }}>🔍</span>
              <input placeholder="Search by name, ID, email, phone…" value={search} onChange={e=>setSearch(e.target.value)}
                style={{...inputStyle,paddingLeft:34,width:'100%',borderRadius:10,boxSizing:'border-box'}} />
            </div>
            <select value={tierFilter} onChange={e=>setTierFilter(e.target.value)} style={{...inputStyle,width:160,borderRadius:10}}>
              <option value="">All Tiers</option>
              {['standard','silver','gold','platinum'].map(t=><option key={t} value={t}>{TIER_EMOJI[t]} {TIER_LABEL[t]}</option>)}
            </select>
          </div>
          {loading ? <div style={{ textAlign:'center',padding:40,color:'var(--textMuted)' }}>Loading profiles…</div>
          : filteredProfiles.length===0 ? <Empty text="No guest profiles found" />
          : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:14 }}>
              {filteredProfiles.map(p=>(
                <div key={p.id} onClick={()=>setSelected(p)}
                  style={{ background:'var(--surface)',borderRadius:14,overflow:'hidden',cursor:'pointer',border:'1px solid rgba(255,255,255,0.06)',transition:'transform 0.14s,box-shadow 0.14s',boxShadow:'0 2px 10px rgba(0,0,0,0.12)' }}
                  onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 8px 28px rgba(0,0,0,0.22)';}}
                  onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,0.12)';}}>
                  <div style={{ height:3, background:TIER_COLOR[p.loyalty_tier] }} />
                  <div style={{ padding:'14px 16px' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12 }}>
                      <div>
                        <div style={{ fontWeight:800,color:'var(--text)',fontSize:15 }}>{p.name}</div>
                        <div style={{ color:'var(--textMuted)',fontSize:12,marginTop:2 }}>{p.id_number}{p.nationality?` · ${p.nationality}`:''}</div>
                      </div>
                      <div style={{ display:'flex',flexDirection:'column',alignItems:'flex-end',gap:4 }}>
                        <TierBadge tier={p.loyalty_tier} size="sm" />
                        <button onClick={e=>openEdit(p,e)} style={{...btnSm,fontSize:10,padding:'2px 8px',opacity:0.7}}>Edit</button>
                      </div>
                    </div>
                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:p.preferences?10:0 }}>
                      {[['Stays',p.total_stays,gold],['Spend',fmtKES(p.total_spend),green],['Pts',p.loyalty_points,blue]].map(([l,v,c])=>(
                        <div key={l} style={{ background:'rgba(255,255,255,0.04)',borderRadius:8,padding:'7px 8px',textAlign:'center' }}>
                          <div style={{ color:'var(--textMuted)',fontSize:10 }}>{l}</div>
                          <div style={{ fontWeight:700,color:c,fontSize:13,marginTop:2 }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {p.preferences && (
                      <div style={{ marginTop:10,fontSize:11,color:'var(--textMuted)',fontStyle:'italic',background:'rgba(255,255,255,0.03)',borderRadius:6,padding:'6px 8px' }}>
                        💭 {p.preferences.slice(0,100)}{p.preferences.length>100?'…':''}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Complaints Tab */}
      {tab==='complaints' && (
        <div style={{ padding:'0 20px' }}>
          {complaints.length===0 ? <Empty text="No complaints logged" /> : (
            <>
              {['open','escalated','in_progress','resolved'].map(status=>{
                const group = complaints.filter(c=>c.status===status);
                if (!group.length) return null;
                return (
                  <div key={status} style={{ marginBottom:24 }}>
                    <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12 }}>
                      <span style={{ fontSize:16 }}>{STATUS_ICON[status]}</span>
                      <span style={{ fontWeight:800,color:STATUS_COLOR[status],fontSize:14,textTransform:'uppercase',letterSpacing:'0.06em' }}>{status.replace('_',' ')}</span>
                      <span style={{ fontSize:12,fontWeight:600,padding:'2px 10px',borderRadius:20,background:`${STATUS_COLOR[status]}18`,color:STATUS_COLOR[status] }}>{group.length}</span>
                    </div>
                    <div style={{ display:'grid', gap:10 }}>
                      {group.map(c=>(
                        <div key={c.id} style={{ background:'var(--surface)',borderRadius:12,padding:'14px 16px',borderLeft:`4px solid ${STATUS_COLOR[c.status]}`,boxShadow:'0 2px 8px rgba(0,0,0,0.1)' }}>
                          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8 }}>
                            <div>
                              <span style={{ fontWeight:700,color:'var(--text)',fontSize:14 }}>{profiles.find(p=>p.id===c.profile)?.name||`Guest #${c.profile}`}</span>
                              <div style={{ marginTop:3,display:'flex',gap:8 }}>
                                <span style={{ fontSize:11,color:STATUS_COLOR[c.status],fontWeight:600,background:`${STATUS_COLOR[c.status]}18`,padding:'2px 8px',borderRadius:20 }}>{c.category.replace('_',' ')}</span>
                                <span style={{ fontSize:11,color:'var(--textMuted)' }}>{c.date_reported}</span>
                              </div>
                            </div>
                            {status!=='resolved' && (
                              <button onClick={()=>resolveComplaint(c.id)} style={{...btnSm,background:`${green}22`,color:green,border:`1px solid ${green}44`,fontSize:11}}>✓ Resolve</button>
                            )}
                          </div>
                          <div style={{ color:'var(--textMuted)',fontSize:13,lineHeight:1.5 }}>{c.description}</div>
                          {c.resolution && (
                            <div style={{ marginTop:8,padding:'6px 10px',background:`${green}0d`,borderRadius:6,color:green,fontSize:12 }}>✓ {c.resolution}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* NPS Tab */}
      {tab==='nps' && (
        <div style={{ padding:'0 20px' }}>
          {summary && (
            <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:14,marginBottom:24 }}>
              <div style={{ background:'var(--surface)',borderRadius:14,padding:'20px 18px',textAlign:'center',border:'1px solid rgba(255,255,255,0.07)' }}>
                <div style={{ color:'var(--textMuted)',fontSize:11,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:12 }}>Net Promoter Score</div>
                <NPSGauge score={summary.nps_score} />
              </div>
              {[['📋 Profiles',summary.total_profiles,gold],['⚠️ Open Issues',summary.open_complaints,summary.open_complaints>0?red:green]].map(([l,v,c])=>(
                <div key={l} style={{ background:'var(--surface)',borderRadius:14,padding:'20px 18px',textAlign:'center',border:'1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ color:'var(--textMuted)',fontSize:11,textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8 }}>{l}</div>
                  <div style={{ fontWeight:900,fontSize:36,color:c }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {profiles.filter(p=>p.nps_scores?.length>0).length===0 ? <Empty text="No NPS scores yet" /> : (
            <div style={{ display:'grid', gap:10 }}>
              {profiles.filter(p=>p.nps_scores?.length>0).map(p=>(
                <div key={p.id} style={{ background:'var(--surface)',borderRadius:12,padding:'14px 16px' }}>
                  <div style={{ fontWeight:700,color:'var(--text)',marginBottom:10,display:'flex',gap:8,alignItems:'center' }}>
                    {p.name} <TierBadge tier={p.loyalty_tier} size="sm" />
                  </div>
                  <div style={{ display:'grid', gap:8 }}>
                    {p.nps_scores.map(n=>{
                      const col=n.score>=9?green:n.score>=7?gold:red;
                      return (
                        <div key={n.id} style={{ display:'flex',gap:12,alignItems:'center',padding:'8px 12px',background:`${col}0a`,borderRadius:8,borderLeft:`3px solid ${col}` }}>
                          <span style={{ fontWeight:900,fontSize:22,color:col,minWidth:32 }}>{n.score}</span>
                          <span style={{ fontSize:11,color:col,fontWeight:700,width:72 }}>{n.score>=9?'Promoter':n.score>=7?'Passive':'Detractor'}</span>
                          <span style={{ color:'var(--textMuted)',flex:1,fontSize:13 }}>{n.comment||'—'}</span>
                          <span style={{ color:'var(--textMuted)',fontSize:11,flexShrink:0 }}>{new Date(n.submitted_at).toLocaleDateString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Summary Tab */}
      {tab==='summary' && summary && (
        <div style={{ padding:'0 20px' }}>
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:14,marginBottom:24 }}>
            {[['👤 Total Profiles',summary.total_profiles,gold],['🔄 Avg Stays',summary.avg_stays,blue],['💰 Avg Spend',fmtKES(summary.avg_spend),green],['📊 NPS Score',summary.nps_score!==null?`${summary.nps_score}`:'N/A',summary.nps_score>=50?green:orange],['⚠️ Open Issues',summary.open_complaints,summary.open_complaints>0?red:green]].map(([l,v,c])=>(
              <div key={l} style={{ background:'var(--surface)',borderRadius:12,padding:'16px 18px',border:'1px solid rgba(255,255,255,0.07)',borderTop:`3px solid ${c}` }}>
                <div style={{ color:'var(--textMuted)',fontSize:12,marginBottom:8 }}>{l}</div>
                <div style={{ fontWeight:900,fontSize:26,color:c }}>{v}</div>
              </div>
            ))}
          </div>
          <Card title="Loyalty Tier Distribution">
            {(summary.by_tier||[]).map(t=>{
              const pct=summary.total_profiles>0?(t.count/summary.total_profiles)*100:0;
              return (
                <div key={t.loyalty_tier} style={{ display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
                  <TierBadge tier={t.loyalty_tier} size="sm" />
                  <div style={{ flex:1,height:8,background:'rgba(255,255,255,0.07)',borderRadius:8,overflow:'hidden' }}>
                    <div style={{ height:'100%',width:`${pct}%`,background:TIER_COLOR[t.loyalty_tier],borderRadius:8,transition:'width 0.5s ease' }} />
                  </div>
                  <span style={{ fontWeight:700,color:'var(--text)',minWidth:30,textAlign:'right',fontSize:14 }}>{t.count}</span>
                  <span style={{ color:'var(--textMuted)',fontSize:12,minWidth:38,textAlign:'right' }}>{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </Card>
        </div>
      )}
    </div>
  );
}
